/**
 * Analytics Relay Service
 *
 * Replaces direct Segment SDK with a localStorage-based queue
 * that relays events through our backend to bypass ad blockers
 */

import { log } from '@/plugins/logger'
import { httpFunctionsAnalytics } from '@/api'
import { auth } from '@/plugins/firebase'

class AnalyticsRelay {
  constructor() {
    this.tabId = `tab_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
    this.QUEUE_KEY_PREFIX = 'analytics_'
    this.FAILED_KEY = 'analytics_failed'
    this.MAX_EVENT_AGE = 48 * 60 * 60 * 1000 // 48 hours in ms
    this.MAX_QUEUE_SIZE = 1000
    this.FLUSH_INTERVAL = 10000 // 10 seconds

    // Store references for cleanup
    this.flushIntervalId = null
    this.visibilityHandler = null
    this.beforeUnloadHandler = null

    // Initialize on next tick to ensure DOM is ready
    setTimeout(() => this.initialize(), 0)
  }

  initialize() {
    // Set up flush interval
    this.flushIntervalId = setInterval(() => this.flush(), this.FLUSH_INTERVAL)

    // Create handlers so we can remove them later
    // Flush on tab visibility change
    this.visibilityHandler = () => {
      if (document.hidden) {
        log.debug('Analytics Relay: Tab hidden, flushing')
        this.flush()
      }
    }

    // Flush on page unload
    this.beforeUnloadHandler = () => {
      log.debug('Analytics Relay: Page unloading, flushing')
      this.flush()
    }

    // Attach event listeners
    document.addEventListener('visibilitychange', this.visibilityHandler)
    window.addEventListener('beforeunload', this.beforeUnloadHandler)
  }

  /**
   * Track an analytics event
   * @param {string} event - Event name
   * @param {Object} properties - Event properties
   */
  track(event, properties = {}) {
    try {
      const eventData = {
        type: 'track',
        event,
        properties,
        timestamp: new Date().toISOString()
      }

      this._addToQueue(eventData)
    } catch (error) {
      log.warn('Analytics Relay: Failed to track event', error)
    }
  }

  /**
   * Track a page view
   * @param {string} name - Page name
   * @param {Object} properties - Page properties
   */
  page(name, properties = {}) {
    try {
      const eventData = {
        type: 'page',
        name,
        properties,
        timestamp: new Date().toISOString()
      }

      this._addToQueue(eventData)
    } catch (error) {
      log.warn('Analytics Relay: Failed to track page', error)
    }
  }

  /**
   * Track an error event with system context
   * NOTE: We treat errors differently than tracks because errors are initialized as a plugin
   * before the auth store is available (in plugins/logger.js)
   * @param {string} message - Error message
   * @param {Object} details - Error details
   */
  async trackError(message, details = null) {
    try {
      let userData = null

      // Try to get user data from auth store if available
      try {
        const { useAuthStore } = await import('@/store/auth')
        const authStore = useAuthStore()
        userData = authStore.userData
      } catch (e) {
        // Store might not be initialized yet, this is fine
      }

      const eventData = {
        type: 'track',
        event: 'Error Logged',
        properties: {
          error_message: message,
          error_details: details,
          user_id: userData?.uid || 'unknown',
          tenant_id: userData?.company || 'unknown',
          email: userData?.email || 'unknown',
          timestamp: new Date().toISOString()
        },
        timestamp: new Date().toISOString()
      }

      this._addToQueue(eventData)
    } catch (error) {
      log.warn('Analytics Relay: Failed to track error', error)
    }
  }

  /**
   * Reset analytics - flush pending events then clear tab queue
   */
  reset() {
    log.debug('Analytics Relay: Reset called - flushing pending events')

    // Try to flush current events first
    this.flush()

    // Clear the flush interval
    clearInterval(this.flushIntervalId)
    this.flushIntervalId = null

    // Remove event listeners
    document.removeEventListener('visibilitychange', this.visibilityHandler)
    this.visibilityHandler = null

    window.removeEventListener('beforeunload', this.beforeUnloadHandler)
    this.beforeUnloadHandler = null

    // Then clear this tab's queue
    const key = `${this.QUEUE_KEY_PREFIX}${this.tabId}`
    localStorage.removeItem(key)

    // Don't clear other tabs or failed events - they might be mid-flush
    log.debug('Analytics Relay: Reset complete - cleaned up listeners and cleared tab queue')
  }

  /**
   * Add event to localStorage queue
   * @private
   */
  _addToQueue(eventData) {
    const key = `${this.QUEUE_KEY_PREFIX}${this.tabId}`
    const queue = this._getQueue(key)

    queue.push(eventData)

    // Limit queue size to prevent unbounded growth
    if (queue.length > this.MAX_QUEUE_SIZE) {
      queue.splice(0, queue.length - this.MAX_QUEUE_SIZE)
    }

    localStorage.setItem(key, JSON.stringify(queue))
  }

  /**
   * Get queue from localStorage
   * @private
   */
  _getQueue(key) {
    try {
      return JSON.parse(localStorage.getItem(key) || '[]')
    } catch (error) {
      log.warn('Analytics Relay: Failed to parse queue', error)
      return []
    }
  }

  /**
   * Flush all queued events to backend
   */
  flush() {
    try {
      // Skip flush if user is not authenticated
      if (!auth.currentUser) {
        log.debug('Analytics Relay: Skipping flush - no authenticated user')
        return
      }

      const allEvents = this._collectAllEvents()

      const validEvents = this._filterValidEvents(allEvents)

      if (validEvents.length === 0) {
        log.debug('No valid events to flush', { count: validEvents.length })
        return
      }

      // Fire-and-forget: deliberately not awaiting to keep flush non-blocking
      this._sendEvents(validEvents)
    } catch (error) {
      log.error('Analytics Relay: Flush failed', error)
    }
  }

  /**
   * Collect all events from failed queue and all tab queues
   * @private
   * @returns {Array<Object>} All collected events
   */
  _collectAllEvents() {
    const allEvents = []

    // First, include any previously failed events
    const failedEvents = this._getQueue(this.FAILED_KEY)
    allEvents.push(...failedEvents)
    localStorage.removeItem(this.FAILED_KEY)
    if (failedEvents.length > 0) {
      // TODO: REMOVE once dev is complete
      log.debug('Analytics Relay: Including failed events', { count: failedEvents.length })
    }

    // Collect events from all tabs
    const keysToRemove = []
    for (const key in localStorage) {
      if (key.startsWith(this.QUEUE_KEY_PREFIX) && key !== this.FAILED_KEY) {
        const events = this._getQueue(key)
        allEvents.push(...events)
        // Always remove tab keys to prevent orphans
        keysToRemove.push(key)
      }
    }

    // Remove processed queues
    keysToRemove.forEach(key => localStorage.removeItem(key))

    return allEvents
  }

  /**
   * Filter out events older than MAX_EVENT_AGE and events without required fields
   * @private
   * @param {Array<Object>} allEvents - All collected events
   * @returns {Array<Object>} Valid events within age limit and with required fields
   */
  _filterValidEvents(allEvents) {
    const now = Date.now()

    const validEvents = allEvents.filter(event => {
      const eventAge = now - new Date(event.timestamp).getTime()
      if (eventAge > this.MAX_EVENT_AGE) {
        log.debug('Analytics Relay: Dropping old event', {
          event: event.event,
          age: Math.round(eventAge / 1000 / 60 / 60) + ' hours'
        })
        return false
      }

      // drop events without email, user id, or tenant id if it is not an error event

      const hasRequiredFields =
        event.properties.email && event.properties.user_id && event.properties.tenant_id
      if (!hasRequiredFields) {
        log.debug('Analytics Relay: Dropping event without email, user id, or tenant id', {
          event: event.event
        })
        return false
      }

      return true
    })

    log.debug('Analytics Relay: Filtered events', {
      total: validEvents.length,
      dropped: allEvents.length - validEvents.length
    })

    return validEvents
  }

  /**
   * Send events to backend (or save for retry on failure)
   * @private
   * @param {Array<Object>} validEvents - Events to send
   */
  async _sendEvents(validEvents) {
    const token = await auth.currentUser.getIdToken()
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }

    // Fire-and-forget HTTP request to analytics relay
    httpFunctionsAnalytics
      .post('/AnalyticsRelay', { events: validEvents }, { headers: headers })
      .then(response => {
        log.debug('Analytics Relay: Events sent successfully', {
          count: validEvents.length,
          response: response.data
        })
      })
      .catch(error => {
        // On failure, save events for retry (cap at MAX_QUEUE_SIZE)
        const eventsToRetry = validEvents.slice(-this.MAX_QUEUE_SIZE)
        localStorage.setItem(this.FAILED_KEY, JSON.stringify(eventsToRetry))
        log.debug('Analytics Relay: Failed to send events, saved for retry', {
          count: eventsToRetry.length,
          error: error.message
        })
      })
  }
}

// Create singleton instance
const analyticsRelay = new AnalyticsRelay()

// Export with same interface as Segment SDK
export default analyticsRelay
